<template>
  <div class="sequence-record-layout">
    <a-alert v-if="declinedSteps.length" type="error" show-icon class="declined-alert">
      <template slot="message">{{ declinedSteps.length }} steps were declined during review.</template>
      <template slot="description">
        <a-button type="link" class="p-0" @click="$emit('scroll-to-declined')">View declined steps</a-button>
      </template>
    </a-alert>

    <section id="associate-completion" class="sequence-section" v-if="associateSteps.length">
      <div class="section-heading">
        <h3>Associate completion</h3>
      </div>
      <div class="step-list step-list">
        <article
          class="step-card"
          :class="{ 'step-card--declined': step.review?.status === 'declined' }"
          v-for="step in associateSteps"
          :key="step.id"
        >
          <div class="step-card__header">
            <div>
              <span class="step-title">
                <a-badge :number-style="{ backgroundColor: '#2563eb' }" :count="step.number" />
                {{ stepName(step) }}
              </span>
              <div class="sequence-muted">{{ stepTypeTitle(step) }}</div>
            </div>
            <div class="step-statuses">
              <a-tag :color="step.status === 'completed' ? '#10b981' : '#f59e0b'" class="m-0">
                {{ SEQUENCE_STEP_STATUS[step.status]?.title || step.status || 'Unknown' }}
              </a-tag>
              <a-tag :color="reviewStatusColor(step.review?.status)" class="m-0">
                {{ reviewStatusTitle(step.review?.status) }}
              </a-tag>
            </div>
          </div>
          <p v-if="step.review?.comment" class="m-0">Reason: {{ step.review.comment }}</p>
          <a-button ghost type="primary" class="self-start" @click="$emit('open-step', step)">
            {{ step.review?.status ? evidenceActionLabel(step) : 'Review' }}
          </a-button>
        </article>
      </div>
    </section>

    <section id="admin-review" class="sequence-section" v-if="targetAdminSteps.length">
      <div class="section-heading">
        <h3>Admin review</h3>
      </div>
      <div v-if="targetAdminSteps.length" class="step-list step-list">
        <article class="step-card" v-for="step in targetAdminSteps" :key="step.id">
          <div class="step-card__header">
            <div>
              <span class="step-title">{{ step.template?.name || '-' }}</span>
              <div class="sequence-muted">{{ step.template?.type?.name || 'Document' }}</div>
            </div>
            <div class="step-statuses">
              <a-tag :color="step.is_required ? '#f59e0b' : '#6b7280'" class="m-0">
                {{ step.is_required ? 'Required' : 'Optional' }}
              </a-tag>
              <a-tag :color="adminRecordStatusColor(step)" class="m-0">
                {{ adminRecordStatusTitle(step) }}
              </a-tag>
            </div>
          </div>
          <a-button
            v-if="step.document"
            ghost
            type="primary"
            class="self-start"
            @click="$emit('open-document-view', step.document)"
          >
            View attached document
          </a-button>
        </article>
      </div>
      <a-alert v-else type="info" message="No admin tasks were required for this sequence." />
    </section>

    <section id="signatures" class="sequence-section">
      <div class="section-heading">
        <h3>Signatures</h3>
      </div>
      <div class="signatures-grid">
        <div>
          <p>Associate signature</p>
          <associate-signature
            v-if="sequence.signature"
            :available-signature="sequence.signature"
            key="associate_signature_view"
          />
          <a-empty v-else description="No signature on file." />
        </div>
        <div>
          <p>Reviewer signature</p>
          <associate-signature
            v-if="sequence.reviewer_signature"
            :available-signature="sequence.reviewer_signature"
            key="reviewer_signature_view"
          />
          <a-empty v-else description="No signature on file." />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Signature from '@/components/signature.vue'
import { Alert, Badge, Button, Empty, Tag } from 'ant-design-vue'
import {
  getAdminRecordStatusColor,
  getAdminRecordStatusTitle,
  getEvidenceTitle,
  getReviewStatusColor,
  getReviewStatusTitle,
  getStepName,
  getStepTypeTitle,
  SEQUENCE_STEP_STATUS
} from './sequence-review.helpers'

export default {
  name: 'SequenceReviewRecord',
  components: {
    'a-alert': Alert,
    'a-badge': Badge,
    'a-button': Button,
    'a-empty': Empty,
    'a-tag': Tag,
    'associate-signature': Signature
  },
  props: {
    sequence: {
      type: Object,
      required: true
    },
    associateSteps: {
      type: Array,
      default: () => []
    },
    targetAdminSteps: {
      type: Array,
      default: () => []
    },
    declinedSteps: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      SEQUENCE_STEP_STATUS
    }
  },
  methods: {
    stepName: getStepName,
    stepTypeTitle: getStepTypeTitle,
    evidenceActionLabel: getEvidenceTitle,
    reviewStatusTitle: getReviewStatusTitle,
    reviewStatusColor: getReviewStatusColor,
    adminRecordStatusTitle: getAdminRecordStatusTitle,
    adminRecordStatusColor: getAdminRecordStatusColor
  }
}
</script>

<style scoped>
.sequence-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sequence-record-layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.section-heading h3 {
  margin: 0;
}

.step-list {
  display: grid;
  grid-auto-rows: max-content;
  gap: 12px;
  max-height: 360px;
  overflow: auto;
}

.step-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--border-color-base);
  border-radius: 8px;
}

.step-card--declined {
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.04);
}

.step-card__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.step-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.step-statuses {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.sequence-muted {
  color: #6b7280;
  font-size: 12px;
}

.declined-alert {
  margin-bottom: 4px;
}

.signatures-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 24px;
}

.signatures-grid > div {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.self-start {
  align-self: flex-start;
}

@media screen and (max-width: 640px) {
  .signatures-grid {
    grid-template-columns: 1fr;
  }

  .step-card__header,
  .section-heading {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
